import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  GraduationCap,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  User,
  Building,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EducationVerify() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  
  // Form state for verification
  const [verifierName, setVerifierName] = useState('');
  const [verifierTitle, setVerifierTitle] = useState('');
  const [disputeNote, setDisputeNote] = useState('');

  useEffect(() => {
    const fetchEntry = async () => {
      try {
        const response = await fetch(`${API_URL}/api/education-verify/${token}`);
        
        if (response.ok) {
          const data = await response.json();
          setEntry(data);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Invalid or expired verification link');
        }
      } catch (err) {
        console.error('Failed to fetch education entry:', err);
        setError('Failed to load verification data');
      } finally {
        setLoading(false);
      }
    };

    fetchEntry();
  }, [token]);

  const handleConfirm = async () => {
    if (!verifierName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/education-verify/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'confirm',
          verifier_name: verifierName,
          verifier_title: verifierTitle,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCompleted(true);
        setResult({ success: true, action: 'confirmed' });
        toast.success('Education record verified successfully');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to verify record');
      }
    } catch (err) {
      console.error('Failed to confirm:', err);
      toast.error('Failed to verify record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeNote.trim()) {
      toast.error('Please provide a reason for the dispute');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/education-verify/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'dispute',
          dispute_note: disputeNote,
        }),
      });

      if (response.ok) {
        setCompleted(true);
        setResult({ success: true, action: 'disputed' });
        toast.success('Dispute recorded');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to record dispute');
      }
    } catch (err) {
      console.error('Failed to dispute:', err);
      toast.error('Failed to record dispute');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Verification Error</h2>
              <p className="text-muted-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              {result?.action === 'confirmed' ? (
                <>
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Verification Complete</h2>
                  <p className="text-muted-foreground">
                    Thank you for verifying {entry?.employee_name}&apos;s education record. 
                    The record has been marked as verified.
                  </p>
                </>
              ) : (
                <>
                  <XCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Dispute Recorded</h2>
                  <p className="text-muted-foreground">
                    Your dispute has been recorded. The education record will remain 
                    marked as self-reported with your note attached.
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="education-verify-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
            <GraduationCap className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">Education Verification Request</h1>
          <p className="text-muted-foreground mt-2">
            A VETT user has requested verification of their education record
          </p>
        </div>

        {/* Entry Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {entry?.employee_name}
            </CardTitle>
            <CardDescription>
              Claims the following education record
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <div className="flex items-start gap-3">
                <Building className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{entry?.institution}</p>
                  <p className="text-sm text-muted-foreground">Institution</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <GraduationCap className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{entry?.degree_label}</p>
                  <p className="text-sm text-muted-foreground">in {entry?.program}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">
                    {entry?.start_date || 'N/A'} - {entry?.end_date || 'Present'}
                  </p>
                  <p className="text-sm text-muted-foreground">Dates Attended</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Verify Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              Confirm Education Record
            </CardTitle>
            <CardDescription>
              If the above information is correct, please confirm by providing your details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="verifier_name">Your Name *</Label>
                <Input
                  id="verifier_name"
                  value={verifierName}
                  onChange={(e) => setVerifierName(e.target.value)}
                  placeholder="e.g., Jane Smith"
                  data-testid="verifier-name-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="verifier_title">Your Title (Optional)</Label>
                <Input
                  id="verifier_title"
                  value={verifierTitle}
                  onChange={(e) => setVerifierTitle(e.target.value)}
                  placeholder="e.g., Associate Registrar"
                  data-testid="verifier-title-input"
                />
              </div>
            </div>
            <Button 
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={handleConfirm}
              disabled={submitting || !verifierName.trim()}
              data-testid="confirm-education-btn"
            >
              {submitting ? 'Verifying...' : 'Confirm Education Record'}
            </Button>
          </CardContent>
        </Card>

        {/* Dispute Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <XCircle className="w-5 h-5" />
              Dispute Record
            </CardTitle>
            <CardDescription>
              If you cannot confirm this record, please provide details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="dispute_note">Reason for Dispute *</Label>
              <Textarea
                id="dispute_note"
                value={disputeNote}
                onChange={(e) => setDisputeNote(e.target.value)}
                placeholder="e.g., No record of this student in our system, or dates do not match our records"
                rows={3}
                data-testid="dispute-note-input"
              />
            </div>
            <Button 
              variant="outline"
              className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={handleDispute}
              disabled={submitting || !disputeNote.trim()}
              data-testid="dispute-education-btn"
            >
              {submitting ? 'Recording...' : 'Record Dispute'}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>
            VETT is a verified professional records platform. Your verification helps 
            employers trust verified work and education histories.
          </p>
        </div>
      </div>
    </div>
  );
}
